import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/OrdersTable.css";
import ConfirmModal from "./ConfirmModal";
import { buildEstadoMercarioTexto } from "./MarketStateBadges";
import OrderFlowStatus from "./OrderFlowStatus";
import "../styles/OrderFlowStatus.css";

const FALLBACK_SYMBOL = "DASH_USDT";

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatNumber = (value, digits = 4) => {
  const numeric = toNumber(value);
  return numeric === null ? "N/A" : numeric.toFixed(digits);
};

const formatCurrency = (value, digits = 4, signed = false) => {
  const numeric = toNumber(value);
  if (numeric === null) return "N/A";
  const prefix = signed && numeric > 0 ? "+" : "";
  return `${prefix}$${numeric.toFixed(digits)}`;
};

const formatPercent = (value, digits = 2) => {
  const numeric = toNumber(value);
  if (numeric === null) return "N/A";
  const prefix = numeric > 0 ? "+" : "";
  return `${prefix}${numeric.toFixed(digits)}%`;
};

const formatDateShort = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatRemaining = (remainingSeconds) => {
  if (remainingSeconds === null || remainingSeconds === undefined || Number.isNaN(remainingSeconds)) {
    return "N/A";
  }
  const total = Math.max(0, Math.floor(remainingSeconds));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatAgeHours = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return "N/A";
  const total = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const getHZRemaining = (order, nowMs) => {
  const expirySeconds = toNumber(order?.hz_vencimiento);
  if (expirySeconds !== null && expirySeconds > 0) {
    return expirySeconds - nowMs / 1000;
  }
  const hzHours = toNumber(order?.hz_horas);
  if (hzHours === null || hzHours <= 0) return null;
  return hzHours * 3600;
};

const getHZGraciaRemaining = (order, nowMs) => {
  const vencimiento = toNumber(order?.hz_vencimiento);
  const graciaLimite = toNumber(order?.hz_gracia_limite);
  if (graciaLimite === null || graciaLimite <= 0) return null;
  const nowSec = nowMs / 1000;
  if (vencimiento !== null && vencimiento > 0 && nowSec < vencimiento) return null;
  const remaining = graciaLimite - nowSec;
  if (remaining <= 0) return 0; // gracia ya expirada
  return remaining;
};

const getOrderValueUsd = (order) => {
  const qty = toNumber(order?.cantidad ?? order?.size);
  const price = toNumber(
    order?.precio_actual ?? order?.precio_compra ?? order?.current_price ?? order?.price
  );
  if (qty === null || price === null) return null;
  return qty * price;
};

const getOrderIdValue = (order) =>
  String(
    order?.orderId ??
      order?.order_id ??
      order?.id ??
      order?._id ??
      order?.mongo_id ??
      ""
  ).trim();

const BASE_COLUMNS = {
  symbol: "Símbolo",
  cantidad: "Cantidad",
  precio_compra: "Compra",
  precio_actual: "Actual",
  max_precio: "Máximo",
  p_l_precio: "P&L",
  p_l_porcentaje: "%",
  sl_price: "SL",
  tp_price: "TP",
  hz_horas: "Hz",
  fecha_compra: "Entrada",
  valor_usd: "Valor USD",
  accion: "Acción",
};

const getSortValue = (order, column) => {
  if (column === "fecha_compra") {
    const ts = new Date(order?.fecha_compra || "").getTime();
    return Number.isFinite(ts) ? ts : 0;
  }

  if (column === "symbol") {
    return String(order?.symbol || "").toUpperCase();
  }

  if (column === "account_id") {
    return String(order?.account_id || order?.user || "").toLowerCase();
  }

  if (column === "valor_usd") {
    const value = getOrderValueUsd(order);
    return value === null ? Number.NEGATIVE_INFINITY : value;
  }

  const numeric = toNumber(order?.[column]);
  return numeric === null ? Number.NEGATIVE_INFINITY : numeric;
};

const getRowTone = (pnlValue) => {
  const pnl = toNumber(pnlValue) ?? 0;
  if (pnl > 0) return "positive";
  if (pnl < 0) return "negative";
  return "flat";
};

const getEntryMethodMeta = (order) => {
  const method = String(order?.entry_method || order?._entry_strategy || "").trim();
  if (!method) return null;
  const m = method.toUpperCase();
  let tone = "info";
  if (m.includes("EXPRESS") || m.includes("ULTRA")) tone = "positive";
  else if (m.includes("REBOTE") || m.includes("NY")) tone = "warning";
  else if (m.includes("IMPULSO")) tone = "neutral";
  else if (m.includes("AGRESIVO") || m.includes("HIBRIDO") || m.includes("RSI45")) tone = "positive";
  return { label: method, tone };
};

const OrdersTable = ({
  data = [],
  onRefresh = () => {},
  loading = false,
  title = "📋 Órdenes Activas",
  symbol = FALLBACK_SYMBOL,
  showUser = false,
  onSell,
  showUserSelector = false,
  currentUser = "",
  adminUsers = [],
  selectedUser = "",
  onUserChange = () => {},
}) => {
  const [lastUpdate, setLastUpdate] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const [sortBy, setSortBy] = useState("fecha_compra");
  const [sortOrder, setSortOrder] = useState("desc");
  const [sellingId, setSellingId] = useState("");
  const [confirmOrder, setConfirmOrder] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef(null);

  const displayOrders = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const columns = useMemo(() => {
    if (!showUser) return BASE_COLUMNS;
    return { account_id: "Usuario", ...BASE_COLUMNS };
  }, [showUser]);

  useEffect(() => {
    if (displayOrders.length > 0) {
      setLastUpdate(new Date());
    }
  }, [displayOrders]);

  useEffect(() => {
    if (!displayOrders.some((order) => toNumber(order?.hz_vencimiento) > 0)) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, [displayOrders]);

  const sortedOrders = useMemo(() => {
    const copy = [...displayOrders];

    copy.sort((a, b) => {
      const aValue = getSortValue(a, sortBy);
      const bValue = getSortValue(b, sortBy);

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return copy;
  }, [displayOrders, sortBy, sortOrder]);

  const stats = useMemo(() => {
    const initial = {
      total: sortedOrders.length,
      totalPnL: 0,
      avgPercent: 0,
      totalUsd: 0,
      positiveCount: 0,
      negativeCount: 0,
    };

    if (sortedOrders.length === 0) return initial;

    const totals = sortedOrders.reduce(
      (acc, order) => {
        const pnl = toNumber(order?.p_l_precio) ?? 0;
        const pnlPercent = toNumber(order?.p_l_porcentaje) ?? 0;
        const usd = getOrderValueUsd(order);

        acc.totalPnL += pnl;
        acc.avgPercent += pnlPercent;
        if (usd !== null) acc.totalUsd += usd;
        if (pnl > 0) acc.positiveCount += 1;
        if (pnl < 0) acc.negativeCount += 1;
        return acc;
      },
      { ...initial }
    );

    totals.avgPercent = totals.avgPercent / sortedOrders.length;
    return totals;
  }, [sortedOrders]);

  const handleSort = (column) => {
    if (column === "accion") return;
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }

    setSortBy(column);
    setSortOrder("desc");
  };

  const selectedSymbol = String(symbol || FALLBACK_SYMBOL).toUpperCase();
  const canSell = typeof onSell === "function";
  const handleSellClick = (order) => {
    const orderId = getOrderIdValue(order);
    if (!canSell || !orderId) return;
    setConfirmOrder(order);
  };

  const handleConfirmSell = async () => {
    const order = confirmOrder;
    if (!order) return;
    const orderId = getOrderIdValue(order);
    setConfirmOrder(null);
    setSellingId(orderId);
    try {
      await onSell({ ...order, orderId });
    } finally {
      setSellingId("");
    }
  };

  const userOptions = useMemo(() => {
    const seen = new Set();
    const list = [];
    const push = (value, label) => {
      if (!seen.has(value)) {
        seen.add(value);
        list.push({ value, label });
      }
    };
    if (currentUser) push(currentUser, `${currentUser.split("@")[0]} (predeterminado)`);
    push("ALL", "TODOS los usuarios");
    (Array.isArray(adminUsers) ? adminUsers : [])
      .filter((u) => u?.username)
      .forEach((u) => push(u.username, u.username.split("@")[0]));
    return list;
  }, [currentUser, adminUsers]);

  const filteredUserOptions = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return userOptions;
    return userOptions.filter((o) =>
      String(o.label).toLowerCase().includes(q) || String(o.value).toLowerCase().includes(q)
    );
  }, [userOptions, userSearch]);

  const currentUserLabel = useMemo(() => {
    const found = userOptions.find((o) => o.value === selectedUser);
    return found ? found.label : (currentUser ? `${currentUser.split("@")[0]} (predeterminado)` : "Mi cuenta");
  }, [userOptions, selectedUser, currentUser]);

  const handleUserSelect = (value) => {
    setUserDropdownOpen(false);
    setUserSearch("");
    onUserChange(value);
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
        setUserSearch("");
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  if (displayOrders.length === 0) {
    return (
      <section className="orders-widget orders-widget-empty">
        <header className="orders-widget-header">
          <div>
            <h3 className="orders-widget-title">{title}</h3>
            <p className="orders-widget-subtitle">{selectedSymbol}</p>
          </div>

          <div className="orders-widget-actions">
            <div className="orders-kpi-row">
              {showUserSelector && (
                <div className="orders-user-selector" ref={userDropdownRef}>
                  <button
                    type="button"
                    className="orders-user-select-btn"
                    onClick={() => setUserDropdownOpen((prev) => !prev)}
                  >
                    <span className="orders-user-select-label">👤 Ver</span>
                    <span className="orders-user-select-value">{currentUserLabel}</span>
                    <span className="orders-user-select-caret">{userDropdownOpen ? "▲" : "▼"}</span>
                  </button>

                  {userDropdownOpen && (
                    <div className="orders-user-dropdown">
                      <input
                        type="text"
                        className="orders-user-search"
                        placeholder="Filtrar usuario..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        autoFocus
                      />
                      <div className="orders-user-options">
                        {filteredUserOptions.length === 0 ? (
                          <div className="orders-user-empty">Sin resultados</div>
                        ) : (
                          filteredUserOptions.map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className={`orders-user-option ${selectedUser === opt.value ? "selected" : ""}`}
                              onClick={() => handleUserSelect(opt.value)}
                            >
                              <span>{opt.label}</span>
                              {selectedUser === opt.value && <span className="orders-user-option-check">✓</span>}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {showUserSelector && <div className="orders-kpi-divider" />}

              <div className="orders-kpi-card">
                <span className="orders-kpi-label">P&L Total</span>
                <span className={`orders-kpi-value ${stats.totalPnL >= 0 ? "positive" : "negative"}`}>
                  {formatCurrency(stats.totalPnL, 2, true)}
                </span>
              </div>

              <div className="orders-kpi-divider" />

              <div className="orders-kpi-card">
                <span className="orders-kpi-label">Promedio</span>
                <span className={`orders-kpi-value ${stats.avgPercent >= 0 ? "positive" : "negative"}`}>
                  {formatPercent(stats.avgPercent, 2)}
                </span>
              </div>

              <div className="orders-kpi-divider" />

              <div className="orders-kpi-card">
                <span className="orders-kpi-label">Valor Total USD</span>
                <span className="orders-kpi-value">{formatCurrency(stats.totalUsd, 2)}</span>
              </div>
            </div>

            <button
              type="button"
              className="orders-refresh-btn"
              onClick={onRefresh}
              disabled={loading}
              title="Actualizar órdenes activas"
            >
              {loading ? "⏳" : "🔄"}
            </button>

            <span className="orders-last-update">{lastUpdate?.toLocaleTimeString() || "--:--:--"}</span>
          </div>
        </header>

        <div className="orders-empty-state">
          <p>📭 Sin órdenes activas</p>
          <button type="button" className="orders-refresh-btn" onClick={onRefresh}>
            🔄 Actualizar
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="orders-widget">
      <header className="orders-widget-header">
        <div>
          <h3 className="orders-widget-title">{title}</h3>
          <p className="orders-widget-subtitle">
            {selectedSymbol} • {sortedOrders.length} {sortedOrders.length === 1 ? "orden activa" : "órdenes activas"}
          </p>
        </div>

        <div className="orders-widget-actions">
          <div className="orders-kpi-row">
            {showUserSelector && (
              <div className="orders-user-selector" ref={userDropdownRef}>
                <button
                  type="button"
                  className="orders-user-select-btn"
                  onClick={() => setUserDropdownOpen((prev) => !prev)}
                >
                  <span className="orders-user-select-label">👤 Ver</span>
                  <span className="orders-user-select-value">{currentUserLabel}</span>
                  <span className="orders-user-select-caret">{userDropdownOpen ? "▲" : "▼"}</span>
                </button>

                {userDropdownOpen && (
                  <div className="orders-user-dropdown">
                    <input
                      type="text"
                      className="orders-user-search"
                      placeholder="Filtrar usuario..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      autoFocus
                    />
                    <div className="orders-user-options">
                      {filteredUserOptions.length === 0 ? (
                        <div className="orders-user-empty">Sin resultados</div>
                      ) : (
                        filteredUserOptions.map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            className={`orders-user-option ${selectedUser === opt.value ? "selected" : ""}`}
                            onClick={() => handleUserSelect(opt.value)}
                          >
                            <span>{opt.label}</span>
                            {selectedUser === opt.value && <span className="orders-user-option-check">✓</span>}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {showUserSelector && <div className="orders-kpi-divider" />}

            <div className="orders-kpi-card">
              <span className="orders-kpi-label">P&L Total</span>
              <span className={`orders-kpi-value ${stats.totalPnL >= 0 ? "positive" : "negative"}`}>
                {formatCurrency(stats.totalPnL, 2, true)}
              </span>
            </div>

            <div className="orders-kpi-divider" />

            <div className="orders-kpi-card">
              <span className="orders-kpi-label">Promedio</span>
              <span className={`orders-kpi-value ${stats.avgPercent >= 0 ? "positive" : "negative"}`}>
                {formatPercent(stats.avgPercent, 2)}
              </span>
            </div>

            <div className="orders-kpi-divider" />

            <div className="orders-kpi-card">
              <span className="orders-kpi-label">Valor Total USD</span>
              <span className="orders-kpi-value">{formatCurrency(stats.totalUsd, 2)}</span>
            </div>
          </div>

          <button
            type="button"
            className="orders-refresh-btn"
            onClick={onRefresh}
            disabled={loading}
            title="Actualizar órdenes activas"
          >
            {loading ? "⏳" : "🔄"}
          </button>

          <span className="orders-last-update">{lastUpdate?.toLocaleTimeString() || "--:--:--"}</span>
        </div>
      </header>

      <div className="orders-table-wrap">
        <table className="orders-table-v2">
          <thead>
            <tr>
              {Object.entries(columns).map(([column, label]) => (
                <th key={column} className={`${column === "symbol" ? "orders-th-symbol" : ""} ${column === "symbol" || column === "fecha_compra" ? "align-left" : "align-right"}`}>
                  {column === "accion" ? (
                    <span className="orders-col-label">{label}</span>
                  ) : (
                    <button
                      type="button"
                      className="orders-sort-btn"
                      onClick={() => handleSort(column)}
                    >
                      <span>{label}</span>
                      <span className="orders-sort-icon">
                        {sortBy === column ? (sortOrder === "asc" ? "▲" : "▼") : "↕"}
                      </span>
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {sortedOrders.map((order, index) => {
              const accountId = String(order?.account_id || order?.user || "");
              const key =
                getOrderIdValue(order) ||
                `${order?.symbol || "ORD"}-${accountId || "user"}-${order?.fecha_compra || index}-${index}`;
              const tone = getRowTone(order?.p_l_precio);
              const entryMethod = getEntryMethodMeta(order);
              const estadoMercarioOrden = buildEstadoMercarioTexto(order?.estado_mercado);

              return (
                <tr key={key} className={`orders-row ${tone}`}>
                  {showUser && (
                    <td className="align-left orders-cell-user" data-label={columns.account_id}>
                      {accountId || "—"}
                    </td>
                  )}
                  <td
                    className="align-left orders-cell-symbol"
                    data-label={columns.symbol}
                  >
                    <div className="orders-symbol-stack">
                        {/* Linea 1: el par. El metodo va en su propia linea debajo, con menos peso visual:
                          identifica como se entro, pero no compite con el simbolo. */}
                      <div className="orders-symbol-head">
                        <span className="orders-symbol-name">
                          {String(order?.symbol || "N/A").toUpperCase()}
                        </span>
                      </div>
                      {entryMethod ? (
                        <span className={`orders-entry-badge tone-${entryMethod.tone}`}>
                          {entryMethod.label}
                        </span>
                      ) : null}
                      {/* Linea 2: los estados, agrupados aparte. El filete a la izquierda
                          los marca como un bloque de informacion sobre la fila y no como
                          una lista de etiquetas sueltas mezcladas con el par. */}
                      <div className="orders-symbol-states">
                        {order?.exchange_bloqueado ? (
                          <span
                            className="orders-exchange-blocked"
                            title={`El exchange tiene este par cerrado y rechazo la venta. Tu capital queda bloqueado hasta la apertura.${
                              order?.exchange_bloqueo_intentos
                                ? ` Intentos de salida rechazados: ${order.exchange_bloqueo_intentos}.`
                                : ""
                            }`}
                          >
                            🔒 Cerrado por mercado — recurso bloqueado hasta la apertura
                          </span>
                        ) : null}
                        {/* Una sola linea de estado por orden. MarketStateBadges queda FUERA a
                            proposito: buildEstadoMercarioTexto ya cubre las mismas claves (exchange
                            primero, luego jornada) y con mejor prioridad. Con los dos, USOX_USDT
                            salia dos veces con el mismo motivo: "Cerrado en Pionex: mercado
                            detenido" y "No negociable: mercado detenido en Pionex".
                            Lo que si se mantiene aparte es el candado de exchange_bloqueado: ese
                            no viene de estado_mercado, es el historial de ventas rechazadas. */}
                        <span
                          className={`market-state-badge tone-${estadoMercarioOrden.tone}`}
                          title={estadoMercarioOrden.detalle}
                        >
                          {estadoMercarioOrden.texto}
                        </span>
                        {/* Profundidad de mercado (Order Flow) - mostrada debajo del estado */}
                        <OrderFlowStatus order={order} />
                      </div>
                    </div>
                  </td>
                  <td className="align-right mono" data-label={columns.cantidad}>
                    {formatNumber(order?.cantidad, 6)}
                  </td>
                  <td className="align-right mono" data-label={columns.precio_compra}>
                    {formatCurrency(order?.precio_compra, 4)}
                  </td>
                  <td className="align-right mono" data-label={columns.precio_actual}>
                    {formatCurrency(order?.precio_actual, 4)}
                  </td>
                  <td className="align-right mono" data-label={columns.max_precio}>
                    {formatCurrency(order?.max_precio, 4)}
                  </td>
                  <td
                    className={`align-right mono orders-cell-pnl ${tone}`}
                    data-label={columns.p_l_precio}
                  >
                    {formatCurrency(order?.p_l_precio, 2, true)}
                  </td>
                  <td
                    className={`align-right mono orders-cell-pnl ${tone}`}
                    data-label={columns.p_l_porcentaje}
                  >
                    {formatPercent(order?.p_l_porcentaje, 2)}
                  </td>
<td className="align-right mono orders-cell-sl" data-label={columns.sl_price}>
                     {order?.sl_inicial_price != null && order.sl_inicial_price !== order.sl_price ? (
                       <div style={{lineHeight:'1.45'}}>
                         <div>
                           <strong>{formatCurrency(order.sl_price, 4)}</strong>
                         </div>
                         <div style={{fontSize:'0.78em',opacity:0.55}}>
                           original {formatCurrency(order.sl_inicial_price, 4)}
                         </div>
                       </div>
                     ) : (
                       <span>{formatCurrency(order?.sl_price, 4)}</span>
                     )}
                   </td>
                  <td className="align-right mono orders-cell-tp" data-label={columns.tp_price}>
                    {formatCurrency(order?.tp_price, 4)}
                  </td>
                  <td className="align-right mono orders-cell-hz" data-label={columns.hz_horas}>
                    {(() => {
                      const ageSec = toNumber(order?.business_age);
                      const hzHours = toNumber(order?.hz_horas);
                      const hzSec = (hzHours !== null && hzHours > 0) ? hzHours * 3600 : null;
                      if (ageSec !== null && ageSec > 0 && hzSec !== null) {
                        const remaining = hzSec - ageSec;
                        if (remaining > 0) {
                          return <span>{formatAgeHours(remaining)}</span>;
                        }
                        const graciaSec = (hzSec * 2) - ageSec;
                        return <span style={{ color: "#ff5c5c", fontWeight: 600 }}>{formatAgeHours(Math.max(0, graciaSec))} (G)</span>;
                      }
                      return formatRemaining(getHZRemaining(order, now));
                    })()}
                  </td>
                  <td className="align-left muted" data-label={columns.fecha_compra}>
                    {formatDateShort(order?.fecha_compra)}
                  </td>
                  <td className="align-right mono" data-label={columns.valor_usd}>
                    {formatCurrency(getOrderValueUsd(order), 2)}
                  </td>
                  <td className="align-right" data-label={columns.accion}>
                    <button
                      type="button"
                      className="orders-sell-btn"
                      onClick={() => handleSellClick(order)}
                      disabled={!canSell || sellingId === getOrderIdValue(order)}
                    >
                      {sellingId === getOrderIdValue(order) ? "⏳" : "Vender"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <footer className="orders-widget-footer">
        <span>📊 {stats.total} abiertas</span>
        <span className="positive">🟢 {stats.positiveCount}</span>
        <span className="negative">🔴 {stats.negativeCount}</span>
        <span>P&L {formatCurrency(stats.totalPnL, 2, true)}</span>
      </footer>

      <ConfirmModal
        open={Boolean(confirmOrder)}
        title="Vender orden"
        message={confirmOrder ? `¿Vender manualmente ${String(confirmOrder.symbol || "").toUpperCase() || "esta orden"}?` : ""}
        confirmLabel="Vender"
        cancelLabel="Cancelar"
        onConfirm={handleConfirmSell}
        onCancel={() => setConfirmOrder(null)}
        loading={Boolean(sellingId)}
      />
    </section>
  );
};

export default React.memo(OrdersTable);
